<script setup lang="ts">
import { onBeforeUnmount, onMounted, ref } from 'vue'
import Box from '@uiKit/elements/Box.vue'
import Drawer from '@uiKit/elements/Drawer.vue'
import { useClickOutside } from '@shared/composables/useClickOutside'
import { useWideViewport } from '../composables/useWideViewport'

// Hosts a filter / sort sheet next to its trigger button.
//   Desktop → dropdown anchored under the trigger, right edges aligned, 8px
//             gap (design: filter dropdown over the card grid).
//   Tablet / phone → bottom sheet with grab handle.
// Closes on outside click, Esc, or when the content calls `close()`.

const { isWide } = useWideViewport()
const isOpen = ref(false)

// Raw <div ref> — the anchor wrapper is measured for outside clicks
// (architecture §19 exception).
const anchorRef = ref<HTMLElement | null>(null)

const open = () => {
  isOpen.value = true
}

const close = () => {
  isOpen.value = false
}

const toggle = () => {
  isOpen.value = !isOpen.value
}

useClickOutside(anchorRef, () => {
  if (isWide.value) close()
})

const handleKeydown = (event: KeyboardEvent) => {
  if (event.key === 'Escape' && isOpen.value) close()
}

onMounted(() => document.addEventListener('keydown', handleKeydown))
onBeforeUnmount(() => document.removeEventListener('keydown', handleKeydown))

defineExpose({ open, close, toggle })
</script>

<template>
  <div ref="anchorRef" class="sheet-host">
    <slot name="trigger" :is-open="isOpen" :toggle="toggle" />

    <Transition name="sheet-host-drop">
      <Box v-if="isWide && isOpen" class="sheet-host__dropdown">
        <slot :is-sheet="false" />
      </Box>
    </Transition>
  </div>

  <Drawer
    v-if="!isWide"
    :model-value="isOpen"
    position="bottom"
    width="100%"
    :show-close-icon="false"
    @update:model-value="(visible) => !visible && close()"
  >
    <slot :is-sheet="true" />
  </Drawer>
</template>

<style scoped>
.sheet-host {
  position: relative;
  display: inline-flex;
  flex-shrink: 0;
}

.sheet-host__dropdown {
  position: absolute;
  top: calc(100% + 8px);
  right: 0;
  z-index: 20;
  border-radius: 16px;
  border: 1px solid var(--p-surface-100);
  background: var(--p-surface-0);
  box-shadow:
    0 12px 32px rgba(16, 24, 40, 0.12),
    0 2px 6px rgba(16, 24, 40, 0.06);
  overflow: hidden;
}

.sheet-host-drop-enter-active,
.sheet-host-drop-leave-active {
  transition:
    opacity 0.15s ease,
    transform 0.15s ease;
}

.sheet-host-drop-enter-from,
.sheet-host-drop-leave-to {
  opacity: 0;
  transform: translateY(-4px);
}

@media (prefers-reduced-motion: reduce) {
  .sheet-host-drop-enter-active,
  .sheet-host-drop-leave-active {
    transition: none;
  }
}
</style>
